import { z } from 'zod';

/**
 * The `ask` channel's events. They are hints: ids, the conversation `version`
 * they reflect, and at most a stage or an error code. Nothing here is ever
 * rendered except `stage`, and nothing here is clinical text.
 *
 * `parse` throws on anything that is not a known v1 event, and the realtime
 * client drops a thrown event while keeping the socket open, so an event type
 * added server-side later is ignored until the app learns it.
 */

const conversationData = z.object({
  conversationId: z.string().min(1),
  version: z.number().int(),
});

const turnData = conversationData.extend({ turnId: z.string().min(1) });

const askEventSchema = z.discriminatedUnion('type', [
  z.object({
    v: z.literal(1),
    type: z.literal('turn.progress'),
    data: turnData.extend({ stage: z.enum(['planning', 'searching', 'grading']) }),
  }),
  z.object({ v: z.literal(1), type: z.literal('turn.completed'), data: turnData }),
  z.object({
    v: z.literal(1),
    type: z.literal('turn.failed'),
    data: turnData.extend({ code: z.string() }),
  }),
  z.object({ v: z.literal(1), type: z.literal('conversation.updated'), data: conversationData }),
  z.object({ v: z.literal(1), type: z.literal('conversation.deleted'), data: conversationData }),
]);

export type AskRealtimeEvent = z.infer<typeof askEventSchema>;

export function parseAskRealtimeEvent(raw: unknown): AskRealtimeEvent {
  return askEventSchema.parse(raw);
}

/** Events after which the history list may have changed order, title, or membership. */
export function changesHistory(event: AskRealtimeEvent): boolean {
  return (
    event.type === 'turn.completed' ||
    event.type === 'conversation.updated' ||
    event.type === 'conversation.deleted'
  );
}

export function askChannelFor(sub: string): string {
  return `/ask/${sub}/*`;
}
